/* eslint-disable react/require-default-props */
import PropTypes from 'prop-types';
import DateFnsUtils from '@date-io/date-fns';
import {
  DateTimePicker,
  DatePicker as DayPicker,
  TimePicker,
  MuiPickersUtilsProvider
} from '@material-ui/pickers';
import styled from '@emotion/styled';
import { fullDateWithTime, getFullDate, getTime } from '@utils/dates';
import Calendar from '@icon/Calendar';
import { createMuiTheme } from '@material-ui/core';
import { ThemeProvider } from '@material-ui/styles';
import OutlinedInput from '@material-ui/core/OutlinedInput';
import { Spacings } from '@styles/index';

const Icon = styled(Calendar)`
  cursor: pointer;
`;

const TextField = ({ disabled, ...props }) => (
  <OutlinedInput
    // eslint-disable-next-line react/jsx-props-no-spreading
    {...props}
    disabled={disabled}
    endAdornment={!disabled && <Icon size="28px" />}
  />
);

const DatePicker = ({
  width,
  gridColumn,
  disabled,
  isInvalid,
  value,
  onChange,
  variant,
  absDate,
  disablePast,
  minDate,
  minTime,
  className,
  maxDate,
  hideDateIfEmpty = false,
  placeHolder = 'N/A'
}) => {
  const materialTheme = createMuiTheme({
    overrides: {
      MuiInputBase: {
        root: {
          width,
          gridColumn,
          border: `1px solid ${
            isInvalid ? 'var(--color-i-red)' : 'var(--color-divider)'
          }`,
          backgroundColor: `${disabled && 'var(--color-base-1)'}`,
          transition: 'border-color 400ms ease-in-out',
          '&:hover': {
            borderColor: `${
              isInvalid ? 'var(--color-i-red)' : 'var(--color-divider)'
            }`
          }
        }
      },
      MuiOutlinedInput: {
        input: {
          padding: `${Spacings.SPACING_4B} ${Spacings.SPACING_6B}`
        },
        adornedEnd: {
          paddingRight: Spacings.SPACING_6B
        },
        notchedOutline: {
          border: 'none'
        }
      },
      MuiPickersToolbar: {
        toolbar: {
          backgroundColor: 'var(--color-i-blue)',
          overflow: 'hidden',
          padding: Spacings.SPACING_4B
        }
      },
      MuiPickersDay: {
        day: {
          color: 'var(--color-text-primary)'
        },
        daySelected: {
          backgroundColor: 'var(--color-i-blue)',
          color: 'var(--color-base-1)'
        },
        dayDisabled: {
          color: 'var(--color-text-secondary)'
        },
        current: {
          color: 'var(--color-text-primary)'
        }
      },
      MuiPickersYear: {
        yearSelected: {
          color: 'var(--color-i-blue)'
        }
      },
      MuiPickersClockNumber: {
        clockNumberSelected: {
          backgroundColor: 'var(--color-i-blue)'
        }
      },
      MuiPickersClockPointer: {
        pointer: {
          backgroundColor: 'var(--color-i-blue)'
        },
        thumb: {
          borderColor: 'var(--color-i-blue)'
        },
        noPoint: {
          backgroundColor: 'var(--color-i-blue)'
        }
      },
      MuiPickersClock: {
        pin: {
          backgroundColor: 'var(--color-i-blue)'
        }
      },
      MuiButton: {
        textPrimary: {
          color: 'var(--color-i-blue)',
          '&:hover': {
            backgroundColor: 'var(--color-base-1)'
          }
        },
        root: {
          '&:hover': {
            backgroundColor: 'var(--color-i-blue)'
          }
        }
      },
      MuiGrid: {
        container: {
          height: '100%'
        },
        'direction-xs-column': {
          flexDirection: 'unset'
        },
        'grid-xs-5': {
          flexDirection: 'column',
          height: 'fit-content'
        },
        'grid-xs-6': {
          height: 'fit-content',
          alignSelf: 'flex-end'
        },
        'grid-xs-1': {
          position: 'absolute',
          maxWidth: 'unset',
          flexDirection: 'row',
          right: '94px',
          width: 'fit-content',
          height: 'fit-content'
        }
      },
      MuiTypography: {
        h3: {
          fontSize: '2.125rem',
          lineHeight: '1.235',
          letterSpacing: '0.00735em'
        }
      },
      MuiPickersBasePicker: {
        pickerView: {
          maxWidth: 'unset'
        }
      },
      MuiPickersCalendarHeader: {
        switchHeader: {
          margin: 'auto',
          maxWidth: '292px'
        }
      },
      MuiTouchRipple: {
        root: {
          display: 'none'
        }
      }
    }
  });

  const date = hideDateIfEmpty && !value ? null : new Date(value || absDate);
  return (
    <MuiPickersUtilsProvider utils={DateFnsUtils}>
      <ThemeProvider theme={materialTheme}>
        {variant === 'dateTime' && (
          <DateTimePicker
            className={className}
            value={date}
            onChange={onChange}
            disablePast={disablePast}
            hideTabs
            disabled={disabled}
            autoOk
            inputVariant="outlined"
            labelFunc={fullDateWithTime}
            wider={false}
            minDate={minDate}
            minTime={minTime}
            TextFieldComponent={(props) => (
              // eslint-disable-next-line react/jsx-props-no-spreading
              <TextField {...props} disabled={disabled} />
            )}
          />
        )}
        {variant === 'date' && (
          <DayPicker
            className={className}
            value={date}
            onChange={onChange}
            autoOk
            labelFunc={getFullDate}
            minDate={minDate}
            maxDate={maxDate}
            TextFieldComponent={(props) => (
              <TextField
                // eslint-disable-next-line react/jsx-props-no-spreading
                {...props}
                disabled={disabled}
                inputProps={{ value: date ? getFullDate(date) : placeHolder }}
              />
            )}
          />
        )}
        {variant === 'time' && (
          <TimePicker
            className={className}
            value={date}
            onChange={onChange}
            labelFunc={!value ? () => '' : getTime}
            disabled={disabled}
            autoOk
            minTime={minTime}
            TextFieldComponent={(props) => (
              // eslint-disable-next-line react/jsx-props-no-spreading
              <TextField {...props} disabled={disabled} />
            )}
          />
        )}
      </ThemeProvider>
    </MuiPickersUtilsProvider>
  );
};

export default DatePicker;

DatePicker.propTypes = {
  width: PropTypes.string.isRequired,
  disabled: PropTypes.bool.isRequired,
  isInvalid: PropTypes.bool,
  value: PropTypes.number,
  onChange: PropTypes.func,
  gridColumn: PropTypes.string,
  fullWidth: PropTypes.bool,
  absDate: PropTypes.number,
  variant: PropTypes.oneOf(['dateTime', 'date', 'time']),
  disablePast: PropTypes.bool,
  className: PropTypes.string,
  maxDate: PropTypes.number,
  minDate: PropTypes.number,
  minTime: PropTypes.number
};

TextField.propTypes = {
  disabled: PropTypes.bool.isRequired,
  fullWidth: PropTypes.bool.isRequired
};

DatePicker.defaultProps = {
  isInvalid: false,
  fullWidth: true,
  absDate: 0,
  value: null,
  onChange: () => {},
  disablePast: true,
  variant: 'dateTime',
  gridColumn: ''
};
